import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { Formik } from 'formik';
import { useEffect, useState } from 'react';
import { Form, Row } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import Input from '../../../components/Input/Input';
import Select from '../../../components/Input/Select';
import { getCities } from '../../../services/commerce.service';
import {
  BusinessCategory,
  City,
  CommerceAddressState,
  NewBranchValues,
  State,
} from '../../../types/add_commerce.types';
import schemas from '../../../utils/schemas';

interface NewBranchProps {
  innerRef: any;
  submitBranch(
    values: NewBranchValues,
    index: number,
    state: State | undefined,
    city: City | undefined,
  ): void;
  index: number;
  branchData: NewBranchValues;
  handleDelete: (index: number) => void;
  states: Array<State>;
  businessCategories: Array<BusinessCategory>;
}

let sameAsBusinessInfo: boolean = false;

function NewBranch({
  innerRef,
  submitBranch,
  index,
  branchData,
  handleDelete,
  states,
  businessCategories,
}: NewBranchProps) {
  const location = useLocation();
  const localState = location.state as CommerceAddressState;

  const [cities, setCities] = useState<Array<City>>([]);
  const [chosenState, setChosenState] = useState<State | undefined>(branchData?.city?.state);

  const fetchCities = async (state: State | undefined) => {
    if (state) {
      const responseCities = await getCities(state.id);
      return setCities(responseCities.data);
    }
  };

  const handleOnChangeState = (
    targetValue: string,
    setFieldValue: (name: string, value: string | number) => void,
  ) => {
    setFieldValue('state', +targetValue);
    setFieldValue('city', '');
    const chosenState = states.find((state: State) => state?.id === +targetValue);
    setChosenState(chosenState || undefined);
  };

  useEffect(() => {
    chosenState && fetchCities(chosenState);
  }, [chosenState]);

  const handleFillWithBranchData = async (setFieldValue: (name: string, value: string | number) => void) => {
    sameAsBusinessInfo = !sameAsBusinessInfo;

    handleOnChangeState(sameAsBusinessInfo ? String(localState?.city?.stateId!) : '', setFieldValue);
    setFieldValue('city', +(sameAsBusinessInfo ? String(localState.city?.id!) : ''));
    setFieldValue('addressStreet', sameAsBusinessInfo ? localState.addressStreet : '');
    setFieldValue('addressNumber', sameAsBusinessInfo ? localState.addressNumber! : '');
    setFieldValue('addressFlatNumber', sameAsBusinessInfo ? localState.addressFlatNumber : '');
    setFieldValue('addressSolar', sameAsBusinessInfo ? localState.addressSolar : '');
    setFieldValue('addressPlot', sameAsBusinessInfo ? localState.addressPlot : '');
  };

  const getSchema = () => {
    if (sameAsBusinessInfo) {
      return schemas.ActivateCommerceSecondStepSameAddress;
    } else {
      return schemas.ActivateCommerceSecondStep;
    }
  };

  return (
    <Formik
      innerRef={innerRef}
      initialValues={{
        fantasyName: branchData?.fantasyName || '',
        businessCategoryId: branchData?.businessCategory?.id.toString() || '',
        state: branchData?.city?.state!,
        city: branchData?.city!,
        addressStreet: branchData?.addressStreet!,
        addressNumber: branchData?.addressNumber!,
        addressFlatNumber: branchData?.addressFlatNumber!,
        addressSolar: branchData?.addressSolar!,
        addressPlot: branchData?.addressPlot!,
      }}
      validationSchema={getSchema}
      onSubmit={(values: NewBranchValues) => submitBranch(values, index, chosenState, values.city)}
      validateOnMount
    >
      {({ values, setFieldValue }) => (
        <Form className="form-row">
          <div className="title-row">
            <p className="input-subtitle">SUCURSAL {index + 1}</p>
            {index > 0 && (
              <FontAwesomeIcon
                className="faTrashCan"
                icon={faTrashCan as IconProp}
                onClick={() => handleDelete(index)}
              />
            )}
          </div>
          <Row>
            <Input
              className={clsx('input', index !== 0 && 'col-md-6')}
              type="text"
              name="fantasyName"
              label="Nombre Fantasía"
              required
              index={index}
            />
            {index !== 0 && (
              <Select
                name="businessCategoryId"
                handleOnChange={(targetValue) => setFieldValue('businessCategoryId', +targetValue)}
                className="col-md-6"
                label="Sub rubro"
                index={index}
              >
                {businessCategories?.map((category: BusinessCategory, index: number) => (
                  <option value={category?.id} key={`${category?.id}_${index}`}>
                    {category?.name}
                  </option>
                ))}
              </Select>
            )}
            {index === 0 && (
              <div className="input col-md-12 checkbox-label form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  name="sameAsBusinessInfo"
                  id="flexSwitchCheckDefault"
                  onClick={() => handleFillWithBranchData(setFieldValue)}
                />
                <label htmlFor="flexSwitchCheckDefault">Dirección igual al Domicilio Principal</label>
              </div>
            )}
            <Select
              name="state"
              handleOnChange={handleOnChangeState}
              className="col-md-6"
              label="Departamento"
              required
              disabled={sameAsBusinessInfo && index === 0}
              index={index}
            >
              {states.map((state: State, index: number) => (
                <option value={state?.id} key={`${state?.id}_${index}`}>
                  {state?.name}
                </option>
              ))}
            </Select>
            <Select
              name="city"
              handleOnChange={(targetValue) => setFieldValue('city', +targetValue)}
              className="col-md-6"
              label="Localidad"
              required
              disabled={sameAsBusinessInfo && index === 0}
              index={index}
            >
              {cities.map((city: City, index: number) => (
                <option value={city?.id} key={`${city?.id}_${index}`}>
                  {city?.name}
                </option>
              ))}
            </Select>
            <Input
              className="input col-12"
              type="text"
              name="addressStreet"
              label="Calle"
              required
              readOnly={sameAsBusinessInfo && index === 0}
              index={index}
            />
            <Input
              className="input col-md-3"
              type="text"
              name="addressNumber"
              label="Nº de puerta"
              required
              readOnly={sameAsBusinessInfo && index === 0}
              index={index}
            />
            <Input
              className="input col-md-3"
              type="number"
              name="addressFlatNumber"
              label="Apto (opcional)"
              readOnly={sameAsBusinessInfo && index === 0}
              index={index}
            />
            <Input
              className="input col-md-3"
              type="number"
              name="addressSolar"
              label="Solar (opcional)"
              readOnly={sameAsBusinessInfo && index === 0}
              index={index}
            />
            <Input
              className="input col-md-3"
              type="number"
              name="addressPlot"
              label="Manzana (opcional)"
              readOnly={sameAsBusinessInfo && index === 0}
              index={index}
            />
          </Row>
        </Form>
      )}
    </Formik>
  );
}

export default NewBranch;
